import {Avatar, Button} from '@mui/material';
import {CheckRounded} from '@mui/icons-material';
import useAuth from '../../../hooks/auth/useAuth';
import {Receipt} from '../../../services/receipt/receiptModel';
import {participantKey} from '../../../services/groups/participants';
import {AVATAR_COLORS, avatarSrc, initials} from '../../../utils/avatar';
import {formatCurrency, formatDate} from '../../../utils/format';
import classes from './SplitSummary.module.scss';
import ReceiptWizardLayout from './ReceiptWizardLayout';

interface SplitSummaryProps {
  practice?: boolean;
  receipt: Receipt;
  currency?: string | null;
  onOpen: () => void;
  onDone: () => void;
}

export default function SplitSummary({receipt, currency, onOpen, onDone, practice = false}: SplitSummaryProps) {
  const {user} = useAuth();
  const receiptCurrency = receipt.currency ?? currency;
  const metadata = [receipt.name, receipt.groupName?.startsWith('id:') ? undefined : receipt.groupName,
    formatDate(receipt.dateCreation)].filter(Boolean).join(' · ');
  return <ReceiptWizardLayout tourStage="saved" title={practice ? 'Practice complete — nothing saved' : 'Receipt saved'}>
    <div className={classes.success}>
      <span className={classes.check}><CheckRounded aria-hidden="true"/></span>
      <h2 id="receipt-saved-title">All done!</h2>
      <p>{metadata}</p>
    </div>
    <section className={classes.card} aria-labelledby="split-summary-title">
      <h2 id="split-summary-title">Split summary</h2>
      <ul className={classes.list}>
        {(receipt.entries ?? []).map((entry, index) => {
          const guest = !!entry.guest || !!entry.participantKey?.startsWith('guest:');
          const ownShare = !practice && !guest && !!user?.id && (entry.user?.id === user.id || entry.participantKey === `user:${user.id}`);
          const name = entry.participantName ?? entry.user?.username ?? entry.guest?.name ?? 'Participant';
          return <li key={entry.participantKey ?? (entry.guest ? participantKey(entry.guest) : entry.user ? participantKey(entry.user) : index)}
            className={`${classes.row} ${ownShare ? classes.own : ''}`}>
            <Avatar aria-hidden="true" src={entry.user ? avatarSrc(entry.user) : undefined}
              sx={{width: 34, height: 34, flexShrink: 0, fontSize: 12, fontWeight: 700, bgcolor: AVATAR_COLORS[index % AVATAR_COLORS.length]}}>
              {initials(name)}
            </Avatar>
            <span className={classes.name}>{name}{ownShare && <small>you</small>}{guest && <small>Guest</small>}</span>
            <strong className={classes.amount}>{formatCurrency(entry.totalPrice, receiptCurrency)}</strong>
          </li>;
        })}
      </ul>
      <div className={classes.total}><span>Total</span><strong>{formatCurrency(receipt.totalPrice, receiptCurrency)}</strong></div>
    </section>
    <p className={classes.notice}>These are <strong>calculated shares only.</strong> PayMe does not process payments or track who has paid.</p>
    {receipt.entries.some(entry => (entry.guest || entry.participantKey?.startsWith('guest:')) && entry.totalPrice !== 0) &&
      <p className={classes.notice}>Anyone with a public receipt link can see guest names and shares.</p>}
    <Button fullWidth variant="contained" onClick={onDone} className={classes.home}>{practice ? 'Scan my own receipt' : 'Back to home'}</Button>
    {(practice || receipt.id != null) && <Button fullWidth onClick={onOpen} className={classes.receipt}>{practice ? 'View subscription plans' : 'View receipt'}</Button>}
  </ReceiptWizardLayout>;
}
